/**
 * Wall-clock date helpers.
 *
 * The API stores timestamps as UTC (`...Z`), but the value is really the
 * user's local wall-clock time: `2026-12-01T09:00:00Z` means 9am on 12/1 in
 * whatever timezone the client is in. Never round-trip these through
 * `new Date(str)` / `toISOString()`, which shift by the UTC offset and can
 * move the date a day ahead or behind.
 */

const WALL_CLOCK_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?)?/;

const pad = (n: number, len = 2) => String(n).padStart(len, '0');

/** Parses an API timestamp as local wall-clock time, ignoring any `Z`/offset. */
export function parseWallClock(
  value: string | Date | null | undefined,
): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const m = WALL_CLOCK_RE.exec(value);
  if (!m) return null;
  const [, y, mo, d, h = '0', mi = '0', s = '0', ms = '0'] = m;
  const date = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s),
    Number(ms.padEnd(3, '0')),
  );
  // reject overflow like month 13 or Feb 30, which Date would roll forward
  if (date.getMonth() !== Number(mo) - 1 || date.getDate() !== Number(d)) {
    return null;
  }
  return date;
}

/** Serializes a Date's local wall-clock time in the API's `...Z` shape. */
export function toWallClockISO(date: Date): string {
  return (
    `${toWallClockDate(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}` +
    `:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}Z`
  );
}

/** Local calendar date as `yyyy-MM-dd`. */
export function toWallClockDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Today's local calendar date as `yyyy-MM-dd`. */
export function todayWallClockDate(): string {
  return toWallClockDate(new Date());
}

/**
 * Parses a real UTC instant into local time. Use this only for system
 * timestamps (`createdOn` / `modifiedOn` on CRM entities), which the server
 * stamps with `DateTime.UtcNow` and Dynamics stores in UTC. Everything else
 * from the API is wall-clock time; use `parseWallClock`.
 */
export function parseUtcInstant(
  value: string | Date | null | undefined,
): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (!WALL_CLOCK_RE.test(value)) return null;

  // a value with no offset is still UTC on the server
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const date = new Date(hasOffset ? value : `${value}Z`);
  return isNaN(date.getTime()) ? null : date;
}
